<template>
    <div class="vlv-container" v-if="showContent" ref="containerRef">

        <div class="vlv-search-container" ref="searchRef">
            <input type="search" v-model="searchTerm" :placeholder="$t('VariantListView.SearchPlaceholder')" class="vlv-search-variations" />
            <button type="button" @click.prevent="scrollToTop" :class="{ 'show': showScrollButton }" class="vlv-scroll-to-top-btn">{{ $t("VariantListView.JumpToTop") }}</button>
        </div>

        <div class="vlv-products-list" ref="productsListRef">
            <div v-for="(product, index) in filteredProducts" :key="product.plid" class="vlv-product-item">
                <div class="vlv-product-header" @click="toggleVariants(index)">
                    <img :src="product.ProductImage" alt="Product Image" v-if="product.ProductImage" />
                    <h3 class="vlv-product-title">{{ product.ProduktDesc1 }} <span class="vlv-product-title-attr-value">{{ groupByAttribute === 1 ? product.Variants[0].AttributtVerdi1Txt : product.Variants[0].AttributtVerdi2Txt }}</span></h3>
                    <div class="vlv-toggle-icon" :class="{ 'expanded': expandedProducts[index] }">
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M7 10l5 5 5-5z" />
                        </svg>
                    </div>
                </div>

                <div v-if="product.Variants && product.Variants.length > 0 && expandedProducts[index]"
                    class="vlv-variants-list">
                    <div class="vlv-variant-headers">
                        <div>{{ $t("VariantListView.ProductInfoHeader") }}</div>
                        <div>{{ pricesIncVat ? $t("PRICE.PricesInclAVT") : $t("PRICE.PricesExclAVT") }}</div>
                        <div>{{ $t("VariantListView.StockHeader") }}</div>
                        <div>{{ $t("VariantListView.QuantityHeader") }}</div>
                        <div>{{ $t("VariantListView.UnitTextHeader") }}</div>
                        <div>{{ $t("VariantListView.BuyButtonHeader") }}</div>
                    </div>
                    <div v-for="variant in product.Variants" :key="variant.VareNr" class="vlv-variant-items">
                        <div class="vlv-variant-name">
                            <div>
                                <a :href="variant.ProductLink">
                                    <p class="variants-list_variant-productdesc2">
                                        {{ variant.ProduktDesc2 }}
                                    </p>
                                    <p class="variants-list_variant-attribute" :class="{ 'vlv-hidden': variant.ProduktDesc2 }">
                                        {{ groupByAttribute === 2 ? variant.AttributtVerdi1Txt : variant.AttributtVerdi2Txt }}
                                    </p>
                                </a>
                            </div>
                            <div class="variants-list_variant-productnr">
                                <span class="variants-list_variant-productnr-label">{{$t("VariantListView.ProductNrLabel")}}</span><span class="variants-list_variant-productnr-value">{{ variant.VareNr }}</span>
                            </div>
                        </div>
                        <div class="vlv-variant-list-price">
                            <div class="current-price-container">
                                <span v-if="showCurrentPriceLabel(variant)" class="vlv-current-price-label">{{ ($t("VariantListView.CurrentPrice")) }}</span><span class="vlv-price-item">{{ variant.Price }}</span>
                            </div>
                            <div v-if="hasOldPrice(variant)" class="old-price-container">
                                <span class="vlv-original-price-label ">{{ ($t("VariantListView.OldPrice")) }}</span><span class="OldPriceLabel vlv-price-item">{{ variant.OldPrice }}</span>
                            </div>
                            <div v-if="hasRecommendedRetailPrice(variant)" class="recommended-retail-price-container">
                                <span class="vlv-rrp-label ">{{ ($t("VariantListView.RecommendedPrice")) }}</span><span>{{ variant.RecommendedRetailPrice }}</span>
                            </div>
                        </div>
                        <div v-html="variant.StockStatus"></div>
                        <div>
                            <QuantityInput
                            v-if="variant.QuantityData && variant.QuantityData.ShowQuantityInput" 
                                v-model="variant.Quantity" 
                                :min="getMinQuantity(variant)" 
                                :max="getMaxQuantity(variant)" 
                                :step="getStepSize(variant)" 
                                :decimals="getDecimals(variant)" 
                            />
                        </div>
                        <div>{{ variant.UnitText }}</div>
                        <div>
                            <button v-if="variant.ShowBuyButton" type="button" class="btn btn-primary" @click.prevent="addToCart(variant)">
                                {{ $t("COMMANDS.Buy") }}
                            </button>
                        </div>
                    </div>

                    <div class="vlv-variant-attributes">
                        <span v-for="attribute in product.CommonAttributes" class="vlv-variant-attribute-item">{{ attribute }}</span>
                    </div>
                </div>

            </div>
        </div>

        <div v-if="isLoading" class="vlv-loading">
            <p>{{ $t("VariantListView.LoadingProducts") }}</p>
        </div>

    </div>
</template>

<script setup>
import VariantViewApi from '../api/variant-view-api';
import ProductInfoApi from '../api/product-info-api';

import { ref, computed, onMounted, onUnmounted, reactive } from 'vue'
import QuantityInput from './components/QuantityInput.vue';

// Reactive state
const searchTerm = ref('');
const productsList = ref([]);
const isLoading = ref(false);
const expandedProducts = reactive({});
const showContent = ref(false);
const groupByAttribute = ref(null);
const pricesIncVat = ref(false);
const searchFields = ref(['ProduktDesc1']);
const showScrollButton = ref(false);

// Template refs
const containerRef = ref(null);
const searchRef = ref(null);
const productsListRef = ref(null);

// Throttle helper for performance
let ticking = false;

const filteredProducts = computed(() => {
    if (!searchTerm.value) {
        return productsList.value;
    }
    const searchLower = searchTerm.value.toLowerCase();
    const searchProduktDesc1 = searchFields.value.includes('ProduktDesc1');
    const searchAttr1 = searchFields.value.includes('AttributtVerdi1Txt');
    const searchAttr2 = searchFields.value.includes('AttributtVerdi2Txt');
    
    return productsList.value.filter(product => {
        // Search in product name if enabled
        if (searchProduktDesc1 && 
            product.ProduktDesc1.toLowerCase().includes(searchLower)) {
            return true;
        }
        
        // Search in variant attributes if enabled
        if (product.Variants && product.Variants.length > 0 && (searchAttr1 || searchAttr2)) {
            return product.Variants.some(variant => {
                if (searchAttr1 && 
                    variant.AttributtVerdi1Txt?.toLowerCase().includes(searchLower)) {
                    return true;
                }
                if (searchAttr2 && 
                    variant.AttributtVerdi2Txt?.toLowerCase().includes(searchLower)) {
                    return true;
                }
                return false;
            });
        }
        
        return false;
    });
});

const toggleVariants = (index) => {
    Object.keys(expandedProducts).forEach(key => {
        if (parseInt(key) !== index) {
            expandedProducts[key] = false;
        }
    });

    expandedProducts[index] = !expandedProducts[index];
};

// Price display helpers
const hasOldPrice = (variant) => {
    return variant.OldPrice && 
           typeof variant.OldPrice === 'string' && 
           variant.OldPrice.trim() !== '';
};

const hasRecommendedRetailPrice = (variant) => {
    return variant.RecommendedRetailPrice && 
           typeof variant.RecommendedRetailPrice === 'string' && 
           variant.RecommendedRetailPrice.trim() !== '';
};

const showCurrentPriceLabel = (variant) => {
    return variant.Price && 
           typeof variant.Price === 'string' && 
           variant.Price.trim() !== '' &&
           hasOldPrice(variant);
};

// quantity code
const getStepSize = (variant) => {
    if (!variant.QuantityData || !variant.QuantityData.ShowQuantityInput) return 1;
    
    const stepSize = parseFloat(variant.QuantityData.StepSize);
    return isNaN(stepSize) ? 1 : stepSize;
};

const getMinQuantity = (variant) => {
    if (!variant.QuantityData || !variant.QuantityData.ShowQuantityInput) return 1;
    
    const initPacksize = parseFloat(variant.QuantityData.InitPacksize);
    return isNaN(initPacksize) ? 1 : initPacksize;
};

const getMaxQuantity = (variant) => {
    if (!variant.QuantityData || !variant.QuantityData.ShowQuantityInput) return 99999;
    
    const maxQuantity = parseFloat(variant.QuantityData.MaxQuantity);
    return isNaN(maxQuantity) ? 99999 : maxQuantity;
};

const getDecimals = (variant) => {
    if (!variant.QuantityData || !variant.QuantityData.ShowQuantityInput) return 0;
    
    const stepSize = variant.QuantityData.StepSize;
    const decimalPart = stepSize.split('.')[1];
    return decimalPart ? decimalPart.length : 0;
};

// Add to cart function
const addToCart = async (variant) => {
    const { Quantity, ProduktLagerID } = variant;
    if (!Quantity || Quantity <= 0) return;

    try {
        PubSub.publish(mcWeb.cartsmall.events.onShowNewItemInCart);
        const response = await ProductInfoApi.addToCart(ProduktLagerID, false, Quantity, false, "VariantListView.vue");

        if (response != null && response.data != null && response.data.Success == true) {
            PubSub.publish(mcWeb.cartsmall.events.onShowNewItemInCartText, { status: true, msg: response.data.PopupText });
            PubSub.publish(mcWeb.cartsmall.events.onUpdateCart);
            PubSub.publish(mcWeb.events.freeShippingTxt.onUpdateFreeShipping);

            if (response.data.MessageToPublish !== null) {
                PubSub.publish(response.data.MessageToPublish, response.data.MessageToPublishData);
            }
        }


    } catch (error) {
        console.error('Error adding to cart:', error);
    }
}

const scrollToTop = () => {
    const appElement = document.querySelector('#variant-list-view-app');
    if (appElement) {
        appElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
};

const updateScrollButton = () => {
    if (!productsListRef.value || !searchRef.value) return;
    
    const listTop = productsListRef.value.getBoundingClientRect().top;
    const searchBottom = searchRef.value.getBoundingClientRect().bottom;
    
    showScrollButton.value = listTop < searchBottom - 300;
    ticking = false;
};

const handleScroll = () => {
    if (!ticking) {
        requestAnimationFrame(updateScrollButton);
        ticking = true;
    }
};

const loadProducts = async () => {
    try {
        isLoading.value = true;

        const appElement = document.querySelector('#variant-list-view-app');
        const plid = appElement?.getAttribute('data-plid');

        if (!plid) {
            console.warn('No plid found in DOM. Skipping API call.');
            productsList.value = [];
            return;
        }

        const templateId = appElement?.getAttribute('data-stock-template-id');
        const groupBy = appElement?.getAttribute('data-group-by-attribute-verdi-id');
        const searchFieldsAttr = appElement?.getAttribute('data-search-fields');

        groupByAttribute.value = groupBy ? parseInt(groupBy) : 1;
        searchFields.value = searchFieldsAttr 
            ? searchFieldsAttr.split(',').map(f => f.trim()) 
            : ['ProduktDesc1'];

        const res = await VariantViewApi.GetGroupedVariants({
            variantMotherPlid: plid,
            templateId: templateId ? parseInt(templateId) : null,
            groupByAttribute: groupByAttribute.value
        });

        if (res.data && res.data.Success) {
            showContent.value = true;
            pricesIncVat.value = res.data.PricesIncVat || false;

            if (res.data.GroupedVariants) {
                productsList.value = res.data.GroupedVariants;

                // Initialize Quantity for each variant
                productsList.value.forEach((product, index) => {
                    if (product.Variants && product.Variants.length > 0) {
                        product.Variants.forEach(variant => {
                            variant.Quantity = getMinQuantity(variant); // Initialize with InitPacksize
                        });
                    }
                    expandedProducts[index] = false;
                });
            } else {
                console.warn('No products found in API response');
            }
        } else {
            showContent.value = false;
            console.warn('API request was not successful');
        }
    } catch (error) {
        console.error('Error loading products:', error);

    } finally {
        isLoading.value = false;
    }
};

onMounted(() => {
    loadProducts();
    window.addEventListener('scroll', handleScroll, { passive: true });
});

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll);
});

</script>

<style scoped>
.vlv-container {
    margin: 0 auto;
    padding: 10px;
}

.vlv-search-container {
    position: sticky;
    top: 0;
    z-index: 10;
}

.vlv-search-variations {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    box-shadow: 0 3px 11px rgba(0, 0, 0, .1);
    box-sizing: border-box;
    padding: 12px 22px;
    width: 100%;
}

.vlv-scroll-to-top-btn {
    position: absolute;
    right: 5%;
    top: 50%;
    transform: translateY(-50%);

    font-size: 1.3rem;

    background-color: transparent;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    padding: .3em 1.7em;
    cursor: pointer;
    white-space: nowrap;
    
    opacity: 0;
    visibility: hidden;
    pointer-events: none;
    transition: opacity 0.3s ease, visibility 0.3s ease, background-color 0.2s ease;
}

.vlv-scroll-to-top-btn.show {
    opacity: 1;
    visibility: visible;
    pointer-events: auto;
}

.vlv-scroll-to-top-btn:hover {
    background-color: #f0f0f0;
}

.vlv-product-header {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    gap: 10px;
    cursor: pointer;
    user-select: none;
    position: relative;
    transition: background-color 0.2s ease;
}

.vlv-hidden {
    display: none;
}

.vlv-product-header:hover {
    background-color: #f8f9fa;
}

.vlv-product-item {
    background: #fff;
    border: 1px solid #e8e8e8;
    border-radius: 4px;
    box-shadow: 0 3px 11px rgba(0, 0, 0, .1);
    margin-top: .5em;
    overflow: hidden;
}

.vlv-toggle-icon {
    margin-left: auto;
    transition: transform 0.3s ease;
    color: #666;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
}

.vlv-toggle-icon.expanded {
    transform: rotate(180deg);
}

.vlv-toggle-icon svg {
    pointer-events: none;
}

.vlv-variants-list {
    padding: 0 1em;
    animation: slideDown 0.3s ease-out;
}

@keyframes slideDown {
    from {
        opacity: 0;
        max-height: 0;
    }

    to {
        opacity: 1;
        max-height: 1000px;
    }
}

.vlv-variant-headers {
    display: grid;
    grid-template-columns: 2fr 2fr 1fr 1fr 1fr 1fr;
    font-weight: bold;
    margin-top: 20px;
    border-bottom: 1px solid #ccc;
    padding-bottom: 5px;
}

.vlv-variant-items {
    display: grid;
    grid-template-columns: 2fr 2fr 1fr 1fr 1fr 1fr;
    align-items: center;
    padding: 10px 0;
    border-bottom: 1px solid #eee;
}

.vlv-variant-name {
    display: flex;
    flex-direction: column;
}

.vlv-variant-list-price {
    display: flex;
    flex-direction: column;
}

.vlv-variant-list-price .OldPriceLabel {
    text-decoration: line-through;
    color: #888;
}

.vlv-original-price-label {
    color: #888;
}

.vlv-variant-attributes {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: .8em;
    padding: .5em 0;
}

.vlv-variant-attribute-item { 
    font-size: 1.1rem;
    background-color: #f3f3f3;
    border-radius: 40px;
    padding: 10px 20px 6px;
    text-align: center;
    display: grid;
    place-content: center;
    text-wrap: nowrap;
}

.vlv-variant-list-price-item {
    white-space: nowrap;
}
</style>